import { useEffect, useRef, useState } from 'react';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import { useAnimatedModal } from '../../../../utils/hooks/useAnimatedModal';
import axios from 'axios';

import { ErrorPage } from '../../../ErrorPage';
import { LoadingPage } from '../../../LoadingPage';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { Button } from '../components/Button';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField } from '../components/CheckboxField';
import { DisclaimerBox } from '../components/DisclaimerBox';
import { DataCard } from '../components/DataCard';
import { CreditCard } from '../components/CreditCard';
import { Callout } from '../components/Callout';
import CheckIcon from './CheckIcon';
import * as S from './styles';

interface SocialSecurityCardsProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
}

const SKIP_WORDS = new Set(['da', 'de', 'do', 'das', 'dos', 'des', 'e', 'a', 'o']);

const formatCardName = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return name;
    const first = parts[0];
    const last = parts[parts.length - 1];
    const middle = parts
        .slice(1, -1)
        .filter((p) => !SKIP_WORDS.has(p.toLowerCase()))
        .map((p) => p[0].toUpperCase());
    return [first, ...middle, last].join(' ');
};

const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPercent = (value: number) =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '% ao mês';

const PromotionScene = ({
    accentColor,
    secondaryColor,
    clientThemeSettings,
    holderName,
    limit,
    onReject,
    onAccept,
}: {
    accentColor: string;
    secondaryColor: string;
    clientThemeSettings: any;
    holderName: string;
    limit: number;
    onReject: () => void;
    onAccept: () => void;
}) => {
    const confirmModal = useAnimatedModal();
    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom();

    return (
        <>
            <S.ScrollContent>
                <S.ProposalHeader $backgroundColor={clientThemeSettings?.background_color}>
                    {clientThemeSettings?.logo_url && (
                        <S.LogoImage src={clientThemeSettings?.monochromatic_logo_url ?? clientThemeSettings.logo_url} alt="logo" />
                    )}
                </S.ProposalHeader>
                <S.DataCardContainer style={{ marginTop: -40 }}>
                    <DisclaimerBox mode="success">
                        Você concluiu a contratação de crédito consignado. Em breve o dinheiro estará
                        disponível na sua conta.
                    </DisclaimerBox>
                </S.DataCardContainer>

                <S.HeroSection style={{ textAlign: 'center', alignItems: 'center' }}>
                    <S.Title $accentColor={accentColor}>
                        Você também possui um <strong>cartão benefício pré-aprovado com limite de{' '}
                        {formatCurrency(limit)}</strong>
                    </S.Title>
                </S.HeroSection>

                <S.CardSection>
                    <CreditCard
                        primaryColor={accentColor}
                        secondaryColor={secondaryColor}
                        textColor={clientThemeSettings.button_font_color}
                        label="Benefício"
                        lastDigits="4897"
                        holderName={holderName}
                        limitValue={formatCurrency(limit)}
                        limitLabel="Limite do seu cartão benefício."
                    />
                </S.CardSection>
                <div ref={contentEndRef} />
            </S.ScrollContent>

            <S.FooterSection>
                <S.DualButtonRow>
                    <Button clientThemeSettings={clientThemeSettings} variant="soft" onClick={hasScrolledToEnd ? confirmModal.open : scrollToBottom}>Não, obrigado(a)</Button>
                    <Button clientThemeSettings={clientThemeSettings} onClick={hasScrolledToEnd ? onAccept : scrollToBottom}>
                        Quero o cartão
                    </Button>
                </S.DualButtonRow>
                <PoweredByQITech />
            </S.FooterSection>

            {confirmModal.show && (
                <S.ModalOverlay $closing={confirmModal.isClosing} onClick={confirmModal.close}>
                    <S.ModalSheet $closing={confirmModal.isClosing} onClick={(e) => e.stopPropagation()}>
                        <S.ModalTitle $accentColor={accentColor}>Tem certeza que deseja recusar?</S.ModalTitle>
                        <S.ModalText>
                            Você perderá a oferta do <strong>cartão benefício consignado pré-aprovado</strong> e
                            não poderá contratá-lo neste momento.
                        </S.ModalText>
                        <S.ModalButtonRow>
                            <Button clientThemeSettings={clientThemeSettings} variant="soft" onClick={onReject}>
                                Não quero o cartão
                            </Button>
                            <Button clientThemeSettings={clientThemeSettings} onClick={confirmModal.close}>
                                Voltar
                            </Button>
                        </S.ModalButtonRow>
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}
        </>
    );
};

const IntroductionScene = ({
    accentColor,
    secondaryColor,
    clientThemeSettings,
    holderName,
    onAccept,
}: {
    accentColor: string;
    secondaryColor: string;
    clientThemeSettings: any;
    holderName: string;
    onAccept: () => void;
}) => {
    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom();

    return (
        <>
            <S.ScrollContent>
                <S.ProposalHeader $backgroundColor={clientThemeSettings?.background_color} $padBottom={24}>
                    {clientThemeSettings?.logo_url && (
                        <S.LogoImage src={clientThemeSettings?.monochromatic_logo_url ?? clientThemeSettings.logo_url} alt="logo" />
                    )}
                    <S.HeaderTitle>
                        Você possui um <strong>cartão benefício consignado</strong> disponível.
                    </S.HeaderTitle>
                    <S.CardWrapper $color={secondaryColor}>
                        <S.GlassyBadge $color={secondaryColor}>
                            <CheckIcon color={secondaryColor} />
                        </S.GlassyBadge>
                        <CreditCard
                            primaryColor={accentColor}
                            secondaryColor={secondaryColor}
                            label="Benefício"
                            lastDigits="4897"
                            holderName={holderName}
                        />
                    </S.CardWrapper>
                    <S.HeaderBodyText>
                        O cartão benefício funciona como um <strong>cartão de crédito convencional</strong>,
                        e o pagamento da fatura é descontado direto do seu benefício do INSS.
                    </S.HeaderBodyText>
                </S.ProposalHeader>

                <S.AdvantagesSection>
                    <S.AdvantagesTitle $accentColor={accentColor} style={{ textAlign: 'center', width: '100%' }}>
                        Vantagens do cartão
                    </S.AdvantagesTitle>
                    <S.AdvantageHighlight
                        $borderColor={clientThemeSettings.background_color}
                        $textColor={clientThemeSettings.font_color}
                    >
                        Saque de <strong>70%</strong> do valor do seu limite
                    </S.AdvantageHighlight>
                    <S.AdvantagesGrid>
                        <S.AdvantageChip $borderColor={clientThemeSettings.background_color} $textColor={clientThemeSettings.font_color}>Seguro de vida</S.AdvantageChip>
                        <S.AdvantageChip $borderColor={clientThemeSettings.background_color} $textColor={clientThemeSettings.font_color}>Auxílio funeral</S.AdvantageChip>
                        <S.AdvantageChip $borderColor={clientThemeSettings.background_color} $textColor={clientThemeSettings.font_color}>Descontos em farmácias</S.AdvantageChip>
                    </S.AdvantagesGrid>
                </S.AdvantagesSection>
                <div ref={contentEndRef} />
            </S.ScrollContent>

            <S.FooterSection>
                <Button clientThemeSettings={clientThemeSettings} onClick={hasScrolledToEnd ? onAccept : scrollToBottom}>
                    Quero contratar
                </Button>
                <PoweredByQITech />
            </S.FooterSection>
        </>
    );
};

const CardInformationScene = ({
    accentColor,
    clientThemeSettings,
    limit,
    deliveryFee,
    cardTermUrl,
    informedConsentTermUrl,
    onAccept,
}: {
    accentColor: string;
    clientThemeSettings: any;
    limit: number;
    deliveryFee: number;
    cardTermUrl: string;
    informedConsentTermUrl: string;
    onAccept: () => void;
}) => {
    const [confirmedTerms, setConfirmedTerms] = useState(false);
    const [confirmedDigitalInvoice, setConfirmedDigitalInvoice] = useState(false);
    const [confirmedConsentTerm, setConfirmedConsentTerm] = useState(false);
    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom();
    const checkboxModal = useAnimatedModal();

    const allChecked =
        confirmedTerms &&
        confirmedDigitalInvoice &&
        (!informedConsentTermUrl || confirmedConsentTerm);
    const handleDownloadTerm = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        try {
            const response = await fetch(cardTermUrl);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'termo-contratacao-cartao-beneficio.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            window.open(cardTermUrl, '_blank');
        }
    };

    const handleDownloadInformedConsentTerm = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        try {
            const response = await fetch(informedConsentTermUrl);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'termo-consentimento-esclarecido-cartao-beneficio.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            window.open(informedConsentTermUrl, '_blank');
        }
    };

    return (
    <>
        <S.Header>
            {clientThemeSettings?.logo_url && (
                <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
            )}
        </S.Header>

        <S.ScrollContent>
            <S.HeroSection>
                <S.Title $accentColor={accentColor}>
                    <strong>Informações da contratação</strong> do seu cartão benefício
                </S.Title>
            </S.HeroSection>

            <S.DataCardContainer>
                <DataCard.Root>
                    <DataCard.Row label="Limite" value={formatCurrency(limit)} color={accentColor} />
                    <DataCard.Row label="Taxa de entrega do cartão" value={formatCurrency(deliveryFee)} color={accentColor} />
                    <DataCard.Row label="Fechamento da fatura" value="Todo dia 6 do mês" color={accentColor} />
                    <DataCard.Row label="Vencimento da fatura" value="Todo dia 9 do mês" color={accentColor} />
                </DataCard.Root>
            </S.DataCardContainer>

            <S.DataCardContainer style={{ marginTop: 12 }}>
                <DisclaimerBox mode="info">
                    Para mais informações, consulte o{' '}
                    <a href={cardTermUrl} onClick={handleDownloadTerm}>
                        termo de contratação do cartão benefício
                    </a>.
                </DisclaimerBox>
            </S.DataCardContainer>

            <S.CheckboxWrapper>
                <CheckboxField
                    id="scene3-terms"
                    checked={confirmedTerms}
                    onChange={(e) => setConfirmedTerms(e.target.checked)}
                    accentColor={accentColor}
                >
                    <strong>Confirmo que li e entendi</strong> as condições de contratação do cartão
                    benefício consignado.
                </CheckboxField>
                <CheckboxField
                    id="scene3-digital-invoice"
                    checked={confirmedDigitalInvoice}
                    onChange={(e) => setConfirmedDigitalInvoice(e.target.checked)}
                    accentColor={accentColor}
                    style={{ marginTop: '8px' }}
                >
                    Aceito receber minha fatura digital através do aplicativo.
                </CheckboxField>
                {informedConsentTermUrl && (
                    <>
                        <S.Separator />
                        <CheckboxField
                            id="scene3-consent-term"
                            checked={confirmedConsentTerm}
                            onChange={(e) => setConfirmedConsentTerm(e.target.checked)}
                            accentColor={accentColor}
                            decorate={true}
                        >
                            <strong>Confirmo que li e concordo</strong> com todas as condições descritas no <a href={informedConsentTermUrl} onClick={handleDownloadInformedConsentTerm}>termo de consentimento esclarecido do cartão benefício.</a>
                        </CheckboxField>
                    </>
                )}
            </S.CheckboxWrapper>
            <div ref={contentEndRef} />
        </S.ScrollContent>

        <S.FooterSection>
            <div
                onClick={!hasScrolledToEnd ? scrollToBottom : !allChecked ? checkboxModal.open : undefined}
                style={{ width: '100%' }}
            >
                <Button
                    clientThemeSettings={clientThemeSettings}
                    disabled={!allChecked}
                    onClick={onAccept}
                >
                    Continuar
                </Button>
            </div>
            <PoweredByQITech />
        </S.FooterSection>

        {checkboxModal.show && (
            <S.ModalOverlay $closing={checkboxModal.isClosing} onClick={checkboxModal.close}>
                <S.ModalSheet $closing={checkboxModal.isClosing} onClick={(e) => e.stopPropagation()}>
                    <S.ModalTitle $accentColor={accentColor}>Confirmações pendentes</S.ModalTitle>
                    <S.ModalText>
                        Para continuar, você precisa <strong>marcar todas as caixas de confirmação</strong> acima.
                    </S.ModalText>
                    <S.ModalButtonRow>
                        <Button clientThemeSettings={clientThemeSettings} onClick={checkboxModal.close}>
                            Entendi
                        </Button>
                    </S.ModalButtonRow>
                </S.ModalSheet>
            </S.ModalOverlay>
        )}
    </>
    );
};

const WithdrawInformationScene = ({
    accentColor,
    clientThemeSettings,
    isChecked,
    setIsChecked,
    depositAmount,
    remainingAmount,
    monthlyCet,
    monthlyInterest,
    cardLimit,
    iof,
    totalAmount,
    numberOfInstallments,
    installmentValue,
    withdrawalTermUrl,
    onAccept,
}: {
    accentColor: string;
    clientThemeSettings: any;
    isChecked: boolean;
    setIsChecked: (v: boolean) => void;
    depositAmount: number;
    remainingAmount: number;
    monthlyInterest: number;
    cardLimit: number;
    monthlyCet: number;
    iof: number;
    totalAmount: number;
    numberOfInstallments: number;
    installmentValue: number;
    withdrawalTermUrl: string;
    onAccept: () => void;
}) => {
    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom();
    const checkboxModal = useAnimatedModal();

    const handleDownload = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        try {
            const response = await fetch(withdrawalTermUrl);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'contrato-saque-cartao-beneficio.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            window.open(withdrawalTermUrl, '_blank');
        }
    };

    return (
    <>
        <S.Header>
            {clientThemeSettings?.logo_url && (
                <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
            )}
        </S.Header>

        <S.ScrollContent>
            <S.HeroSection style={{ marginTop: 0 }}>
                <S.Title $accentColor={accentColor}>
                    <strong>Saque</strong>
                </S.Title>
                <S.BodyText $accentColor={accentColor} style={{ marginTop: 8 }}>
                    70% do valor do seu limite do cartão benefício consignado será depositado na sua
                    conta de recebimento do benefício INSS.
                </S.BodyText>
            </S.HeroSection>

            <S.Wrapper>
                <Callout
                    mode="info"
                    accentColor={accentColor}
                    title={formatCurrency(depositAmount)}
                    description="Serão depositados na sua conta de recebimento do benefício."
                />
                <Callout
                    mode="default"
                    title={formatCurrency(remainingAmount)}
                    description="Disponível para compra no seu cartão."
                />

                <DataCard.Root>
                    <DataCard.Row label="Limite do cartão" value={formatCurrency(cardLimit)} color={accentColor} />
                    <DataCard.Row label="Valor do saque" value={formatCurrency(totalAmount)} color={accentColor} />
                    <DataCard.Row label="IOF" value={formatCurrency(iof)} color={accentColor} />
                    <DataCard.Row label="Valor a ser depositado" value={formatCurrency(depositAmount)} color={accentColor} />
                    <DataCard.Row label="Valor disponível para compras" value={formatCurrency(remainingAmount)} color={accentColor} />
                    {monthlyInterest &&
                        <DataCard.Row label="Juros" value={formatPercent(monthlyInterest)} color={accentColor} />
                    }
                    <DataCard.Row label="CET (a.m)" value={formatPercent(monthlyCet)} color={accentColor} />
                    <DataCard.Row label="Quantidade de descontos" value={String(numberOfInstallments)} color={accentColor} />
                    <DataCard.Row label="Valor mensal descontado" value={formatCurrency(installmentValue)} color={accentColor} />
                </DataCard.Root>

                <S.DisclaimerWrapper>
                    <DisclaimerBox mode="away" title="Atenção">
                        {formatCurrency(installmentValue)} serão descontados do seu benefício INSS
                        por {numberOfInstallments} meses.{'\n\n'}Certifique-se de que isso cabe no
                        seu orçamento mensal.
                    </DisclaimerBox>

                    <DisclaimerBox mode="info">
                        Para mais informações, consulte o{' '}
                        <a href={withdrawalTermUrl} onClick={handleDownload}>
                            contrato referente ao saque
                        </a>.
                    </DisclaimerBox>
                </S.DisclaimerWrapper>
            </S.Wrapper>

            <S.CheckboxWrapper>
                <CheckboxField
                    id="scene4-terms"
                    checked={isChecked}
                    onChange={(e) => setIsChecked(e.target.checked)}
                    accentColor={accentColor}
                >
                    <strong>Estou ciente</strong> de que o saque de 70% do valor do limite do cartão
                    benefício será realizado para a conta de recebimento do benefício INSS.
                </CheckboxField>
            </S.CheckboxWrapper>
            <div ref={contentEndRef} />
        </S.ScrollContent>

        <S.FooterSection>
            <div
                onClick={!hasScrolledToEnd ? scrollToBottom : !isChecked ? checkboxModal.open : undefined}
                style={{ width: '100%' }}
            >
                <Button
                    clientThemeSettings={clientThemeSettings}
                    disabled={!isChecked}
                    onClick={onAccept}
                >
                    Continuar
                </Button>
            </div>
            <PoweredByQITech />
        </S.FooterSection>

        {checkboxModal.show && (
            <S.ModalOverlay $closing={checkboxModal.isClosing} onClick={checkboxModal.close}>
                <S.ModalSheet $closing={checkboxModal.isClosing} onClick={(e) => e.stopPropagation()}>
                    <S.ModalTitle $accentColor={accentColor}>Confirmação pendente</S.ModalTitle>
                    <S.ModalText>
                    Para continuar, você precisa <strong>marcar todas as caixas de confirmação</strong> acima.
                    </S.ModalText>
                    <S.ModalButtonRow>
                        <Button clientThemeSettings={clientThemeSettings} onClick={checkboxModal.close}>
                            Entendi
                        </Button>
                    </S.ModalButtonRow>
                </S.ModalSheet>
            </S.ModalOverlay>
        )}
    </>
    );
};

const TOTAL_SCENES = 4;

export const CreditOperationAndSocialSecurityCardsSocialSecurityCards = ({
    signData,
    setSignerStep,
    setSignData,
}: SocialSecurityCardsProps) => {
    const { clientThemeSettings } = useThemeContext();
    const initialError = { has_error: false, status: 0 };

    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(false);
    const [sceneIndex, setSceneIndex] = useState(0);
    const [checkboxState, setCheckboxState] = useState<Record<number, boolean>>({});

    const scrollContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        scrollContentRef.current?.scrollTo({ top: 0 });
    }, [sceneIndex]);

    const card = signData?.social_security_benefit_card ?? {};
    const withdrawal = card?.withdrawal ?? {};
    const holderName: string = formatCardName(signData?.signer_data?.name ?? '');

    const handleComplete = async (decision: 'accepted' | 'rejected') => {
        const payload = {
            step: 'social_security_cards',
            event_date: new Date().toISOString(),
            decision,
        };

        const config = { headers: { Authorization: signData.signer_token } };

        setLoading(true);

        try {
            const response = await axios.patch('/signer', payload, config);
            setSignData({ ...signData, ...response.data });
            setSignerStep(response.data.next_step);
        } catch (err: any) {
            setError({
                has_error: true,
                status: [401, 403, 409].includes((err.response || {}).status)
                    ? err.response.status
                    : 500,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleReject = () => handleComplete('rejected');

    const handleAccept = () => {
        if (sceneIndex < TOTAL_SCENES - 1) {
            setCheckboxState({});
            setSceneIndex(sceneIndex + 1);
        } else {
            handleComplete('accepted');
        }
    };

    const accentColor = clientThemeSettings?.font_color || '#0e3573';
    const secondaryColor = clientThemeSettings?.button_color || accentColor;

    if (loading) {
        return <LoadingPage clientThemeSettings={clientThemeSettings} />;
    }

    const isChecked = !!checkboxState[sceneIndex];
    const setIsChecked = (v: boolean) =>
        setCheckboxState((prev) => ({ ...prev, [sceneIndex]: v }));

    const scenes = [
        <PromotionScene
            key={0}
            accentColor={accentColor}
            secondaryColor={secondaryColor}
            clientThemeSettings={clientThemeSettings}
            holderName={holderName}
            limit={card.limit}
            onReject={handleReject}
            onAccept={handleAccept}
        />,
        <IntroductionScene
            key={1}
            accentColor={accentColor}
            secondaryColor={secondaryColor}
            clientThemeSettings={clientThemeSettings}
            holderName={holderName}
            onAccept={handleAccept}
        />,
        <CardInformationScene
            key={2}
            accentColor={accentColor}
            clientThemeSettings={clientThemeSettings}
            limit={card.limit}
            deliveryFee={card.delivery_fee}
            cardTermUrl={card.term_document_url}
            informedConsentTermUrl={card.informed_consent_term_document_url}
            onAccept={handleAccept}
        />,
        <WithdrawInformationScene
            key={3}
            accentColor={accentColor}
            clientThemeSettings={clientThemeSettings}
            isChecked={isChecked}
            setIsChecked={setIsChecked}
            depositAmount={withdrawal.deposit_amount}
            remainingAmount={withdrawal.remaining_amount}
            monthlyInterest={withdrawal.monthly_interest}
            monthlyCet={withdrawal.monthly_cet}
            cardLimit={card.limit}
            iof={withdrawal.iof}
            totalAmount={withdrawal.total_amount}
            numberOfInstallments={withdrawal.number_of_installments}
            installmentValue={withdrawal.installment_value}
            withdrawalTermUrl={withdrawal.term_document_url}
            onAccept={handleAccept}
        />,
    ];

    return (
        <S.PageWrapper>
            {scenes[sceneIndex]}

            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}
        </S.PageWrapper>
    );
};
